/**
 * The Settings account page: her time zone, and a coach's optional-email
 * choices. Business logic lives in `~/lib/server/coach-email-preferences` and
 * `~/lib/server/user`.
 *
 * Aliases: account settings api, email preferences api, time zone api
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  EMAIL_CATEGORY_KEYS,
  type EmailPreferenceState,
} from "~/lib/shared/coach-email-preferences";
import { timeZoneSettingSchema } from "~/lib/shared/time-zone-setting";

const emailPreferenceChangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("category"),
    category: z.enum(EMAIL_CATEGORY_KEYS),
    enabled: z.boolean(),
  }),
  z.object({ kind: z.literal("weekly_check_in"), enabled: z.boolean() }),
  z.object({ kind: z.literal("pause") }),
  z.object({ kind: z.literal("resume") }),
]);

export const getOwnTimeZoneFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<string | null> => {
    const { getUserTimeZone } = await import("~/lib/server/user");
    return getUserTimeZone(context.user.id);
  });

export const saveOwnTimeZoneFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ timeZone: timeZoneSettingSchema }))
  .handler(async ({ data, context }): Promise<string | null> => {
    const [{ saveUserTimeZone }, { rescheduleNovaRoutinesForUser }] =
      await Promise.all([
        import("~/lib/server/user"),
        import("~/lib/server/nova/routines/engine"),
      ]);
    const timeZone = await saveUserTimeZone(context.user.id, data.timeZone);
    // Her NOVA routines run on her clock, so they move with the new zone.
    await rescheduleNovaRoutinesForUser(context.user.id);
    return timeZone;
  });

export const getCoachEmailPreferencesFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<EmailPreferenceState> => {
    const { getCoachEmailPreferences } =
      await import("~/lib/server/coach-email-preferences");
    return getCoachEmailPreferences(context.coach.id);
  });

/**
 * Save the change, then push it to GoHighLevel straight away. A failed push
 * stays on the row for the 15-minute sweep; she sees her choice saved either
 * way, because the app's row is what counts.
 */
export const changeCoachEmailPreferencesFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(emailPreferenceChangeSchema)
  .handler(async ({ data, context }): Promise<EmailPreferenceState> => {
    const { changeCoachEmailPreferences, pushCoachEmailPreferences } =
      await import("~/lib/server/coach-email-preferences");
    const preferences = await changeCoachEmailPreferences(
      context.coach.id,
      data,
    );
    await pushCoachEmailPreferences(context.coach.id).catch(async (error) => {
      const { reportServerError } = await import("~/lib/server/report-error");
      reportServerError(error, {
        area: "coach_email_preferences",
        tags: { operation: "inline_push", coachId: context.coach.id },
      });
    });
    return preferences;
  });

/**
 * Whether GoHighLevel's do-not-disturb blocks her email, read after the page
 * renders; the page only shows a notice, and the app never clears
 * do-not-disturb. The contact search does not answer with do-not-disturb, so
 * the contact it finds is read again by id.
 */
export const getCoachEmailDoNotDisturbFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<boolean> => {
    const [
      { findCoachContact, resolvePrimaryGhlAccess },
      { getContact },
      { blocksEmail },
    ] = await Promise.all([
      import("~/lib/server/coach-email-preferences"),
      import("~/lib/ghl/get-contact"),
      import("~/lib/ghl/contact.model"),
    ]);
    const access = await resolvePrimaryGhlAccess();
    const found = await findCoachContact(context.coach.email, access);
    if (!found) return false;
    return blocksEmail(await getContact(found.contactId, access.accessToken));
  });
